import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Sparkles } from 'lucide-react';

const MealSwapModal = ({ open, onClose, currentMeal, alternativesGrouped = {}, onSwap, swapInfo = null }) => {
  if (!currentMeal) return null;

  const handleSwap = (newMeal) => {
    onSwap(newMeal);
    onClose();
  };

  const { preferred = [], similar = [], explore = [], strictMode = true } = alternativesGrouped;
  const totalAlternatives = preferred.length + similar.length + explore.length;

  // Render a meal card
  const renderMealCard = (meal) => (
    <button
      key={meal.id}
      onClick={() => handleSwap(meal)}
      className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-all group text-left"
    >
      <div className="flex items-start gap-3">
        <span className="text-3xl">{meal.emoji}</span>
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-semibold text-gray-900 group-hover:text-purple-700">
                {meal.name}
              </h4>
              <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
                <span>{meal.calories} cal</span>
                <span>•</span>
                <span>{meal.protein}g protein</span>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-purple-600 flex-shrink-0" />
          </div>
          
          {/* Smart Labels */}
          {meal.smartLabels && meal.smartLabels.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {meal.smartLabels.map((label, idx) => (
                <span
                  key={idx}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${label.color}`}
                >
                  <span>{label.icon}</span>
                  {label.text}
                </span>
              ))}
            </div>
          )}
          
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-xs">
              {meal.cuisine.replace(/_/g, ' ')}
            </Badge>
            {meal.tags.slice(0, 2).map((tag, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                {tag.replace(/_/g, ' ')}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            Swap Your Meal
          </DialogTitle>
          <DialogDescription>
            Choose an alternative that matches your health goals and preferences
            {swapInfo && (
              <span className="block mt-2 text-sm font-medium text-indigo-600">
                {swapInfo.is_premium 
                  ? '✨ Unlimited swaps available' 
                  : `💫 ${swapInfo.swaps_remaining} free swap${swapInfo.swaps_remaining !== 1 ? 's' : ''} left today`
                }
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Current Meal */}
        <div className="py-4">
          <p className="text-sm font-semibold text-gray-700 mb-3">Current Meal:</p>
          <div className="p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
            <div className="flex items-start gap-3">
              <span className="text-3xl">{currentMeal.emoji}</span>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900">{currentMeal.name}</h4>
                <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
                  <span>{currentMeal.calories} cal</span>
                  <span>•</span>
                  <span>{currentMeal.protein}g protein</span>
                </div>
                <Badge variant="outline" className="mt-2 text-xs">
                  {currentMeal.cuisine.replace(/_/g, ' ')}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Alternative Meals - SECTIONS */}
        <div className="space-y-4">
          {totalAlternatives === 0 ? (
            <p className="text-center text-gray-500 py-8">No alternatives available</p>
          ) : (
            <>
              {/* SECTION 1: From Your Preferred Cuisine */}
              {preferred.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xl">🏠</span>
                    <h3 className="text-sm font-bold text-purple-700">From Your Preferred Cuisine</h3>
                    <span className="text-xs text-gray-500">({preferred.length} options)</span>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {preferred.map(meal => renderMealCard(meal))}
                  </div>
                </div>
              )}

              {/* SECTION 2: Similar Cuisines */}
              {similar.length > 0 && !strictMode && (
                <div>
                  <div className="flex items-center gap-2 mb-3 pt-4 border-t">
                    <span className="text-xl">🌍</span>
                    <h3 className="text-sm font-bold text-teal-700">Similar Cuisines</h3>
                    <span className="text-xs text-gray-500">({similar.length} options)</span>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {similar.map(meal => renderMealCard(meal))}
                  </div>
                </div>
              )}

              {/* SECTION 3: Explore Something New */}
              {explore.length > 0 && !strictMode && (
                <div>
                  <div className="flex items-center gap-2 mb-3 pt-4 border-t">
                    <span className="text-xl">✨</span>
                    <h3 className="text-sm font-bold text-amber-700">Explore Other Cultures</h3>
                    <span className="text-xs text-gray-500">({explore.length} options)</span>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {explore.map(meal => renderMealCard(meal))}
                  </div>
                </div>
              )}

              {/* Strict Mode Indicator */}
              {strictMode && preferred.length > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-4">
                  <p className="text-xs text-purple-700 flex items-center gap-2">
                    <span>🔒</span>
                    <span><strong>Strict Preference Mode:</strong> Showing only meals from your selected cuisines. Turn off strict mode in preferences to see more variety.</span>
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Cancel Button */}
        <div className="pt-4 border-t">
          <Button onClick={onClose} variant="outline" className="w-full">
            Keep Current Meal
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MealSwapModal;
